import classNames from "classnames";
import Link from "next/link";
import { Icon, IconName, ServiceIcon, Text } from "@zapier/design-system";
import { IconButton } from "@zapier/design-system-beta";
import {
  editingNodeAtom,
  NodeType,
  DefaultNodeData,
  ZapMetadata,
  floatingToolbarActiveButtonAtom,
  serviceAtomFamily,
  AssetType,
} from "@zapier/canvas-state";
import { useAtomValue, useSetAtom } from "jotai";
import { NodeProps, Node } from "@xyflow/react";

import { StepTitle } from "../../../../components/StepTitle";
import capitalizeFirstLetter from "../../../../utils/capitalizeFirstLetter";
import { determineAssetType } from "../../../../utils/determineAssetType";
import { usePreviewContext } from "../../../../context/PreviewProvider";
import { lightenColor } from "../../../../utils/lightenColor";
import { UnstyledButton } from "../../../../components/UnstyledButton/UnstyledButton";

import styles from "./AssetNodeSkeleton.module.css";
import { AssetPreview } from "./AssetPreview";
import { getContrastingTextColor } from "./utils";

type Props = {
  className: string;
  placeholder: string;
  zap?: ZapMetadata;
  isZapExpanded?: boolean;
  isZapNotFound?: boolean;
  isViewOnlyMode?: boolean;
  iconName?: IconName;
};

export function AssetNodeSkeleton(
  props: NodeProps<Node<DefaultNodeData>> & Props,
) {
  const { isPreview } = usePreviewContext();
  const {
    id,
    data,
    type,
    selected,
    className,
    isViewOnlyMode = false,
    iconName,
  } = props;
  const { link, color } = data;
  const appId = data.services?.[0]?.currentImplementation.id ?? "";
  const setEditingNode = useSetAtom(editingNodeAtom);
  const openEditModal = () => {
    setEditingNode(id);
  };
  const setActiveButton = useSetAtom(floatingToolbarActiveButtonAtom);
  const app = useAtomValue(serviceAtomFamily(appId));
  const assetType = determineAssetType(data.services, type as NodeType);
  const asset = getAssetId(data, assetType);

  const openFloatingLinkInput = () => {
    setActiveButton("link");
  };

  return (
    <div
      className={classNames(
        styles.container,
        styles[assetType || ""],
        className,
      )}
      style={{
        borderColor: color,
        backgroundColor: lightenColor(color!, 0.2, 1.2),
      }}
    >
      <div className={styles.body}>
        <div
          className={styles.previewImage}
          style={{ backgroundColor: `#${app?.primary_color}` }}
        >
          {assetType && <AssetPreview type={assetType} />}
          {app?.images?.url_128x128 && !assetType && (
            <ServiceIcon
              serviceName={app?.name}
              size="large"
              src={app?.images?.url_128x128}
            />
          )}
          {!isViewOnlyMode && !isPreview && (
            <span className={styles.editButton}>
              <IconButton
                name="actionEditAi"
                size="medium"
                variant="primary"
                onPress={openEditModal}
              />
            </span>
          )}
        </div>
        <StepTitle
          nodeId={id}
          label={data.label}
          isSelected={Boolean(selected)}
          placeholder={props.placeholder}
          canNodeGetAiSuggestion={false}
          rows={2}
          isViewOnlyMode={isViewOnlyMode || isPreview}
        />
      </div>
      <div className={styles.footer} style={{ backgroundColor: color }}>
        {iconName && (
          <div className={styles.iconContainer}>
            <Icon name={iconName} color="BrandOrange" size={16} />
          </div>
        )}
        {app?.images?.url_16x16 && !assetType && (
          <ServiceIcon
            serviceName={app?.name}
            size="small"
            src={app?.images?.url_16x16}
          />
        )}
        {!link && (
          <Text type="MinimalPrint1" color={getContrastingTextColor(color!)}>
            {capitalizeFirstLetter((assetType || app?.name) ?? "")}
          </Text>
        )}
        {link && (
          <>
            <UnstyledButton onClick={openFloatingLinkInput}>
              <Text
                color={getContrastingTextColor(color!)}
                type="MinimalPrint1"
              >
                {link}
              </Text>
            </UnstyledButton>
            <Link href={link} target="_blank" className={styles.offsiteLink}>
              <Icon name="arrowOffsite" size={15} />
            </Link>
          </>
        )}
        {assetType && !asset && (
          <UnstyledButton onClick={openFloatingLinkInput}>
            <Text
              type="MinimalPrint1"
              color={getContrastingTextColor(color!)}
              className={styles.underline}
            >
              Link existing {capitalizeFirstLetter(assetType)}
            </Text>
          </UnstyledButton>
        )}
      </div>
    </div>
  );
}

function getAssetId(data: any, assetType: AssetType) {
  if (assetType === "zap") {
    return data.zapId;
  }
  if (assetType === "table") {
    return data.tableId;
  }
  if (assetType === "interface") {
    return data.pageId;
  }
  if (assetType === "chatbot") {
    return data.chatbotId;
  }
  return null;
}
